import React, { useCallback } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { useModal } from '../../hooks/use-modal';
import { ICourseData } from '../../types/course-data';
import { CourseCard } from './catalog-course-card';
import { ModalDetails } from '../modal/modal-details';
import { generateCourseColorScheme } from '../../utils/colors';
import { CourseType } from '../../types/course';
import slugify from 'slugify';
import { EventBus } from '@/shared/utils';
import { CatalogEvents } from '../../events';
import { formatDateToCustomString } from 'src/modules/catalog/utils/date-format';

interface CardWithModalProps extends ICourseData {
  viewLessonCallback: (title: string) => void;
  blockOpen?: boolean;
  eventBus: EventBus<CatalogEvents>;
  onFavoriteChange?: (courseId: string, favouriteId: string | undefined) => void;
  isEnrichingData?: boolean;
}

const CardWithModal: React.FC<CardWithModalProps> = ({
  blockOpen,
  viewLessonCallback,
  eventBus,
  onFavoriteChange,
  isEnrichingData = false,
  ...courseData
}) => {
  const { isOpen, openModal, closeModal } = useModal();

  // Handle favorite change from modal - moved before early return
  const handleFavoriteChange = useCallback(
    (courseId: string, favouriteId: string | undefined) => {
      // Only allow favorite changes for non-serie course types
      if (courseData.courseType?.toLowerCase() !== 'serie') {
        // Pass the change up to parent components to update enriched data
        if (onFavoriteChange && typeof onFavoriteChange === 'function') {
          onFavoriteChange(courseId, favouriteId);
        }
      }
    },
    [onFavoriteChange, courseData.courseType],
  );

  // Verifica che i dati del corso siano validi
  const isValidCourse =
    courseData &&
    typeof courseData === 'object' &&
    'documentId' in courseData &&
    courseData.documentId &&
    'title' in courseData &&
    courseData.title &&
    'courseType' in courseData &&
    courseData.courseType;

  // Se i dati non sono validi, mostra un fallback
  if (!isValidCourse) {
    console.warn('Invalid course data detected:', courseData);
    return (
      <Card className="w-full max-w-[318px] md:max-w-full mx-auto h-[334px] md:h-[352px] relative bg-paper">
        <CardContent className="flex flex-col items-center justify-center h-full">
          <Typography variant="body1" color="textSecondary">
            Dati corso non disponibili
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const addQueryParam = (key: string, value: string) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.pushState({}, '', url.toString());
  };

  const removeQueryParam = (key: string) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.pushState({}, '', url.toString());
  };

  const handleClick = (courseData: ICourseData) => {
    // Verifica ancora che i dati siano validi al momento del click
    if (!courseData || !courseData.documentId || !courseData.title) {
      console.warn('Attempted to open modal with invalid course data');
      return;
    }

    if (
      (!courseData.isUpcoming && courseData.isPlayable) ||
      courseData.courseType.toLowerCase() !== 'masterclass'
    ) {
      openModal();
    }

    if (courseData.isUpcoming && !courseData.isPlayable && courseData.linkLive) {
      eventBus.emit('catalog/navigate/do', { slug: courseData.linkLive, isExternalPage: true });
    }
  };

  const handleIsLockedClick = () => {
    eventBus.emit('catalog/navigate/do', { slug: '/abbonati' });
  };

  const handleCloseModal = () => {
    closeModal();
  };

  // Assicuriamoci che abbiamo un courseType valido prima di generare lo schema di colori
  const validCourseType = ['serie', 'percorsi', 'masterclass'].includes(
    courseData.courseType?.toLowerCase() || '',
  )
    ? (courseData.courseType?.toLowerCase() as CourseType)
    : 'masterclass';

  const colorScheme = generateCourseColorScheme(!!courseData.highlighted, validCourseType);

  if (courseData.title.toLowerCase() === 'Suonare il Basso con il Plettro'.toLowerCase()) {
    console.log('Suonare il Basso con il Plettro:', courseData);
  }
  return (
    <>
      <CourseCard
        {...courseData}
        upComingEvent={
          courseData.isUpcoming ? formatDateToCustomString(courseData.date || null) : ''
        }
        colorScheme={colorScheme}
        onClick={() => handleClick(courseData)}
        onIsLockedClick={handleIsLockedClick}
        eventBus={eventBus}
        isEnrichingData={isEnrichingData}
      />

      {isValidCourse && (
        <ModalDetails
          favouriteId={courseData.favouriteId || ''}
          trailerUrl=""
          dialogIsOpen={isOpen}
          dialogToggle={handleCloseModal}
          viewLessonCallback={viewLessonCallback}
          eventBus={eventBus}
          colorScheme={colorScheme}
          onFavoriteChange={handleFavoriteChange}
          {...courseData}
        />
      )}
    </>
  );
};

export default CardWithModal;
